/**
 * Canonical capability catalogue. Mirrors `org.nikita.app.authorization.Capability.ALL`
 * on the backend — backend is the source of truth, frontend keeps this in sync
 * for the template editor UI (CapabilityCatalogueGrid).
 *
 * Format: `resource:verb` strings. Backend rejects any cap not in `ALL` at
 * template create/update with 400 `unknownCapability`.
 */
export type Capability =
  // Guests
  | "guests:read"
  | "guests:read.companions"
  | "guests:read.duplicates"
  | "guests:read.qr-token"
  | "guests:write"
  | "guests:delete"
  | "guests:check-in"
  | "guests:export"
  // Dashboards
  | "dashboards:read"
  | "dashboards:write"
  // Admin surface
  | "event-roles:read"
  | "event-roles:write"
  | "event-role-templates:read"
  | "event-role-templates:write"
  | "event-role-tokens:read"
  | "event-role-tokens:write"
  | "additional-fields:read"
  | "additional-fields:write"
  | "additional-fields:delete"
  | "event:settings:read"
  | "event:settings:write"
  // Automation
  | "workflows:read"
  | "workflows:write"
  | "workflows:delete"
  // Communications
  | "email-templates:read"
  | "email-templates:write"
  | "email-templates:delete"
  | "sent-emails:read"
  | "sent-emails:send"
  | "sender-addresses:read"
  | "email-domains:read"
  // Website
  | "website:read"
  | "website:write"
  // Slotting
  | "slotting:read"
  | "slotting:write"
  | "slotting:delete"
  // Seating
  | "seating:read"
  | "seating:write"
  | "seating:delete"
  // Check-in operations
  | "check-in-devices:read"
  | "check-in-devices:write"
  | "check-in-devices:delete"
  | "check-in-rules:read"
  | "check-in-rules:write"
  | "check-in-logs:read"
  // ADMIN and up. Deliberately NOT in CAPABILITY_CATALOGUE: the audit endpoint refuses
  // EVENT-CUSTOM by role, so a checkbox for it in the template editor would do nothing.
  | "event-audit:read"
  // Attachments + shared links
  | "attachments:read"
  | "attachments:write"
  | "attachments:delete"
  | "shared-links:read"
  | "shared-links:write"
  | "shared-links:revoke"
  // Documents
  | "documents:read"
  | "documents:write"
  | "documents:delete"
  | "documents:export"
  // Event goals & KPIs
  | "event-goals:read"
  | "event-goals:write"
  // Content hub — programme, speakers, sponsors
  | "event-program:read"
  | "event-program:write"
  | "event-program:delete"
  | "event-speakers:read"
  | "event-speakers:write"
  | "event-speakers:delete"
  | "event-sponsors:read"
  | "event-sponsors:write"
  | "event-sponsors:delete"
  // Event setup journey (Setup Hub)
  | "event-setup:read"
  | "event-setup:write"
  // AI assistant
  | "ai:use"
  // OWNER-only fence
  | "owner-only:transfer";

/**
 * Grouped catalogue used by the CapabilityCatalogueGrid in the template
 * editor. Each entry carries i18n label keys + an optional `implies` chain so
 * ticking `guests:write` auto-ticks `guests:read`.
 */
export interface CapabilityDescriptor {
  key: Capability;
  group: CapabilityGroup;
  /** i18n key path under `capabilities.<group>.<verb>`. */
  i18nKey: string;
  /** Caps this one implies — auto-ticked in the UI. */
  implies?: Capability[];
  /** Soft warning when ticked alone (e.g. delete without write). */
  warnIfMissing?: Capability[];
  /**
   * Guest fields the capability's list ACTION reads to work at all. Only
   * meaningful against a RESTRICTED `fieldAllowlist` — an unrestricted role
   * sees every field. The template editor warns when a ticked capability names
   * a field the allowlist withholds (see {@link missingFieldPrerequisites}).
   */
  requiresFields?: string[];
}

export type CapabilityGroup =
  | "guests"
  | "dashboards"
  | "automation"
  | "communications"
  | "website"
  | "slotting"
  | "seating"
  | "checkIn"
  | "attachments"
  | "sharedLinks"
  | "documents"
  | "settings"
  | "goals"
  | "content"
  | "setup"
  | "ai"
  | "ownerOnly";

export const CAPABILITY_CATALOGUE: CapabilityDescriptor[] = [
  // Guests group
  { key: "guests:read", group: "guests", i18nKey: "capabilities.guests.read" },
  { key: "guests:read.companions", group: "guests", i18nKey: "capabilities.guests.readCompanions", implies: ["guests:read"] },
  { key: "guests:read.duplicates", group: "guests", i18nKey: "capabilities.guests.readDuplicates", implies: ["guests:read"] },
  { key: "guests:read.qr-token", group: "guests", i18nKey: "capabilities.guests.readQrToken", implies: ["guests:read"] },
  // The registrations/companions "Aktion" cell decides what it may offer from
  // `acceptanceState`; without it every approve/decline reads "state unknown".
  { key: "guests:write", group: "guests", i18nKey: "capabilities.guests.write", implies: ["guests:read"], requiresFields: ["acceptanceState"] },
  // The guest list's check-in button reads `checkedIn` to know whether it
  // checks in or out; masked, it always reads "not checked in".
  // A check-in without a scanned QR — the cockpit's check-in button — needs
  // `guests:read.qr-token` too (GuestResource check-in-out: a tokenless call is
  // only taken from a caller who may mint tokens); only a warning, a door role
  // that scans signed codes works without it.
  { key: "guests:check-in", group: "guests", i18nKey: "capabilities.guests.checkIn", implies: ["guests:read"], warnIfMissing: ["guests:read.qr-token"], requiresFields: ["checkedIn"] },
  { key: "guests:delete", group: "guests", i18nKey: "capabilities.guests.delete", implies: ["guests:read"], warnIfMissing: ["guests:write"] },
  { key: "guests:export", group: "guests", i18nKey: "capabilities.guests.export", implies: ["guests:read"] },

  // Dashboards group
  { key: "dashboards:read", group: "dashboards", i18nKey: "capabilities.dashboards.read" },
  { key: "dashboards:write", group: "dashboards", i18nKey: "capabilities.dashboards.write", implies: ["dashboards:read"] },

  // Automation group
  { key: "workflows:read", group: "automation", i18nKey: "capabilities.automation.read" },
  { key: "workflows:write", group: "automation", i18nKey: "capabilities.automation.write", implies: ["workflows:read"] },
  { key: "workflows:delete", group: "automation", i18nKey: "capabilities.automation.delete", implies: ["workflows:read"], warnIfMissing: ["workflows:write"] },

  // Communications group
  { key: "email-templates:read", group: "communications", i18nKey: "capabilities.communications.emailTemplatesRead" },
  { key: "email-templates:write", group: "communications", i18nKey: "capabilities.communications.emailTemplatesWrite", implies: ["email-templates:read"] },
  { key: "email-templates:delete", group: "communications", i18nKey: "capabilities.communications.emailTemplatesDelete", implies: ["email-templates:read"], warnIfMissing: ["email-templates:write"] },
  { key: "sent-emails:read", group: "communications", i18nKey: "capabilities.communications.sentEmailsRead" },
  { key: "sent-emails:send", group: "communications", i18nKey: "capabilities.communications.sentEmailsSend", implies: ["email-templates:read"] },
  { key: "sender-addresses:read", group: "communications", i18nKey: "capabilities.communications.senderAddressesRead" },
  { key: "email-domains:read", group: "communications", i18nKey: "capabilities.communications.emailDomainsRead" },

  // Website group
  { key: "website:read", group: "website", i18nKey: "capabilities.website.read" },
  { key: "website:write", group: "website", i18nKey: "capabilities.website.write", implies: ["website:read"] },

  // Slotting group
  { key: "slotting:read", group: "slotting", i18nKey: "capabilities.slotting.read" },
  { key: "slotting:write", group: "slotting", i18nKey: "capabilities.slotting.write", implies: ["slotting:read"] },
  { key: "slotting:delete", group: "slotting", i18nKey: "capabilities.slotting.delete", implies: ["slotting:read"], warnIfMissing: ["slotting:write"] },

  // Seating group
  { key: "seating:read", group: "seating", i18nKey: "capabilities.seating.read" },
  { key: "seating:write", group: "seating", i18nKey: "capabilities.seating.write", implies: ["seating:read"] },
  { key: "seating:delete", group: "seating", i18nKey: "capabilities.seating.delete", implies: ["seating:read"], warnIfMissing: ["seating:write"] },

  // Check-In group — devices, rules, logs
  { key: "check-in-devices:read", group: "checkIn", i18nKey: "capabilities.checkIn.devicesRead" },
  { key: "check-in-devices:write", group: "checkIn", i18nKey: "capabilities.checkIn.devicesWrite", implies: ["check-in-devices:read"] },
  { key: "check-in-devices:delete", group: "checkIn", i18nKey: "capabilities.checkIn.devicesDelete", implies: ["check-in-devices:read"], warnIfMissing: ["check-in-devices:write"] },
  { key: "check-in-rules:read", group: "checkIn", i18nKey: "capabilities.checkIn.rulesRead" },
  { key: "check-in-rules:write", group: "checkIn", i18nKey: "capabilities.checkIn.rulesWrite", implies: ["check-in-rules:read"] },
  { key: "check-in-logs:read", group: "checkIn", i18nKey: "capabilities.checkIn.logsRead" },

  // Attachments group
  { key: "attachments:read", group: "attachments", i18nKey: "capabilities.attachments.read" },
  { key: "attachments:write", group: "attachments", i18nKey: "capabilities.attachments.write", implies: ["attachments:read"] },
  { key: "attachments:delete", group: "attachments", i18nKey: "capabilities.attachments.delete", implies: ["attachments:read"], warnIfMissing: ["attachments:write"] },

  // Shared links group
  { key: "shared-links:read", group: "sharedLinks", i18nKey: "capabilities.sharedLinks.read" },
  // Minting a guest's portal link also needs the QR right (AttachmentResource
  // portal mint refuses a caller without `guests:read.qr-token`).
  { key: "shared-links:write", group: "sharedLinks", i18nKey: "capabilities.sharedLinks.write", implies: ["shared-links:read"], warnIfMissing: ["guests:read.qr-token"] },
  { key: "shared-links:revoke", group: "sharedLinks", i18nKey: "capabilities.sharedLinks.revoke", implies: ["shared-links:read"] },

  // Documents group
  { key: "documents:read", group: "documents", i18nKey: "capabilities.documents.read" },
  { key: "documents:write", group: "documents", i18nKey: "capabilities.documents.write", implies: ["documents:read"] },
  { key: "documents:delete", group: "documents", i18nKey: "capabilities.documents.delete", implies: ["documents:read"], warnIfMissing: ["documents:write"] },
  { key: "documents:export", group: "documents", i18nKey: "capabilities.documents.export", implies: ["documents:read"] },

  // Settings group — exposed but typically not granted to CUSTOM roles
  { key: "additional-fields:read", group: "settings", i18nKey: "capabilities.settings.additionalFieldsRead" },
  { key: "additional-fields:write", group: "settings", i18nKey: "capabilities.settings.additionalFieldsWrite", implies: ["additional-fields:read"] },
  { key: "additional-fields:delete", group: "settings", i18nKey: "capabilities.settings.additionalFieldsDelete", implies: ["additional-fields:read"], warnIfMissing: ["additional-fields:write"] },
  { key: "event:settings:read", group: "settings", i18nKey: "capabilities.settings.eventSettingsRead" },
  { key: "event:settings:write", group: "settings", i18nKey: "capabilities.settings.eventSettingsWrite", implies: ["event:settings:read"] },
  { key: "event-roles:read", group: "settings", i18nKey: "capabilities.settings.eventRolesRead" },

  // Event goals & KPIs group
  { key: "event-goals:read", group: "goals", i18nKey: "capabilities.goals.read" },
  { key: "event-goals:write", group: "goals", i18nKey: "capabilities.goals.write", implies: ["event-goals:read"] },

  // Content hub group — programme, speakers, sponsors
  { key: "event-program:read", group: "content", i18nKey: "capabilities.content.programmeRead" },
  { key: "event-program:write", group: "content", i18nKey: "capabilities.content.programmeWrite", implies: ["event-program:read"] },
  { key: "event-program:delete", group: "content", i18nKey: "capabilities.content.programmeDelete", implies: ["event-program:read"], warnIfMissing: ["event-program:write"] },
  { key: "event-speakers:read", group: "content", i18nKey: "capabilities.content.speakersRead" },
  { key: "event-speakers:write", group: "content", i18nKey: "capabilities.content.speakersWrite", implies: ["event-speakers:read"] },
  { key: "event-speakers:delete", group: "content", i18nKey: "capabilities.content.speakersDelete", implies: ["event-speakers:read"], warnIfMissing: ["event-speakers:write"] },
  { key: "event-sponsors:read", group: "content", i18nKey: "capabilities.content.sponsorsRead" },
  { key: "event-sponsors:write", group: "content", i18nKey: "capabilities.content.sponsorsWrite", implies: ["event-sponsors:read"] },
  { key: "event-sponsors:delete", group: "content", i18nKey: "capabilities.content.sponsorsDelete", implies: ["event-sponsors:read"], warnIfMissing: ["event-sponsors:write"] },

  // Event setup journey group
  { key: "event-setup:read", group: "setup", i18nKey: "capabilities.setup.read" },
  { key: "event-setup:write", group: "setup", i18nKey: "capabilities.setup.write", implies: ["event-setup:read"] },

  // AI assistant — without this entry no CUSTOM template could ever grant
  // ai:use through the UI (backend accepts it, FE would just never offer it).
  { key: "ai:use", group: "ai", i18nKey: "capabilities.ai.use" },

  // Owner-only fence
  { key: "owner-only:transfer", group: "ownerOnly", i18nKey: "capabilities.ownerOnly.transfer" },
];

/**
 * Capabilities that exist in the catalogue (to mirror the backend's
 * `Capability.ALL`) but can NEVER be granted to a role template. The OWNER
 * holds these implicitly and they are not delegable — the backend rejects any
 * template create/update carrying one with 400 `ownerOnlyCapability`. They
 * stay in {@link CAPABILITY_CATALOGUE} so authz lookups (read-only classifier,
 * OWNER cap set) and tests can still reason about them, but the template
 * editor must NOT offer them as tickable options — otherwise the user is given
 * a checkbox that is guaranteed to fail on save.
 */
export const OWNER_ONLY_CAPABILITIES: ReadonlySet<Capability> = new Set<Capability>([
  "owner-only:transfer",
]);

/**
 * Capabilities the backend gives EVERY custom role whatever its template says
 * (`EventRoleAuthorizer.CUSTOM_FOUNDATION_CAPS`): the event's basic read, which
 * the event layout needs to mount at all. The role editor shows them on and
 * locked — a switch that looks revocable but is not would be a lie.
 */
export const CUSTOM_FOUNDATION_CAPABILITIES: ReadonlySet<Capability> = new Set<Capability>([
  "event:settings:read",
]);

/** True when a capability may be granted to a role template via the editor. */
export function isAssignableCapability(cap: Capability): boolean {
  return !OWNER_ONLY_CAPABILITIES.has(cap);
}

/**
 * Catalogue entries the template editor is allowed to surface — i.e. every cap
 * minus the owner-only fence. This is the list the CapabilityCatalogueGrid must
 * render so it never presents an option the backend will reject.
 */
export const ASSIGNABLE_CAPABILITY_CATALOGUE: CapabilityDescriptor[] =
  CAPABILITY_CATALOGUE.filter((d) => isAssignableCapability(d.key));

/**
 * Expand a capability set: include every cap implied (transitively) by any
 * ticked cap. Used by RoleTemplateEditor on save to auto-include implied caps.
 */
export function expandImplied(caps: Set<Capability>): Set<Capability> {
  const out = new Set<Capability>(caps);
  let changed = true;
  while (changed) {
    changed = false;
    for (const desc of CAPABILITY_CATALOGUE) {
      if (!out.has(desc.key) || !desc.implies) continue;
      for (const dep of desc.implies) {
        if (!out.has(dep)) {
          out.add(dep);
          changed = true;
        }
      }
    }
  }
  return out;
}

export interface CapabilityWarning {
  cap: Capability;
  missing: Capability;
}

/**
 * Returns soft warnings: e.g. `guests:delete` without `guests:write`.
 */
export function validateCapabilitySet(caps: Set<Capability>): CapabilityWarning[] {
  const warnings: CapabilityWarning[] = [];
  for (const desc of CAPABILITY_CATALOGUE) {
    if (!caps.has(desc.key) || !desc.warnIfMissing) continue;
    for (const required of desc.warnIfMissing) {
      if (!caps.has(required)) warnings.push({ cap: desc.key, missing: required });
    }
  }
  return warnings;
}

/** A ticked capability whose list action needs a field the allowlist withholds. */
export interface FieldPrerequisiteWarning {
  cap: Capability;
  field: string;
}

/**
 * Which `requiresFields` a RESTRICTED allowlist fails to grant for the caps
 * ticked (implied caps included — `guests:write` implied by nothing today, but
 * the closure is what gets saved). `null` allowlist = unrestricted = nothing
 * missing. Order follows the catalogue, so the editor's hint list is stable.
 */
export function missingFieldPrerequisites(
  caps: Set<Capability>,
  fieldAllowlist: readonly string[] | null,
): FieldPrerequisiteWarning[] {
  if (fieldAllowlist === null) return [];
  const granted = new Set(fieldAllowlist);
  const effective = expandImplied(caps);
  const missing: FieldPrerequisiteWarning[] = [];
  for (const desc of CAPABILITY_CATALOGUE) {
    if (!effective.has(desc.key) || !desc.requiresFields) continue;
    for (const field of desc.requiresFields) {
      if (!granted.has(field)) missing.push({ cap: desc.key, field });
    }
  }
  return missing;
}

/**
 * Which capability each presentation-only "Aktion" column follows. The three
 * columns share one label in every list ("Aktion"): the guest list's cell is
 * the check-in button, the registrations' and companions' cells are
 * approve/decline. None of them is allowlist-governed
 * (`ALLOWLIST_EXEMPT_FIELDS`) — the capability alone shows the button.
 */
export const ACTION_COLUMN_CAPABILITY: Readonly<Record<string, Capability>> = {
  checkInOutCTA: "guests:check-in",
  actionRegistrant: "guests:write",
  actionCompanion: "guests:write",
};

/** What one click on the picker's "Aktion" entry turns on. */
export interface ActionBundle {
  /** Capabilities behind the action columns, catalogue order, deduped. */
  caps: Capability[];
  /** Fields those capabilities' actions read (`requiresFields`), deduped. */
  fields: string[];
}

/**
 * The bundle for a SUBSET of action capabilities, catalogue order. Used by the
 * view import, which knows exactly which "Aktion" columns the saved view
 * carries and must not turn on more than those.
 */
export function actionBundleFor(capKeys: Iterable<Capability>): ActionBundle {
  const wanted = new Set(capKeys);
  const caps: Capability[] = [];
  const fields: string[] = [];
  const seen = new Set<string>();
  for (const desc of CAPABILITY_CATALOGUE) {
    if (!wanted.has(desc.key)) continue;
    caps.push(desc.key);
    for (const field of desc.requiresFields ?? []) {
      if (seen.has(field)) continue;
      seen.add(field);
      fields.push(field);
    }
  }
  return { caps, fields };
}

/** The bundle for the "Aktion" columns a saved view carries (unknown columns ignored). */
export function actionBundleForColumns(columns: Iterable<string>): ActionBundle {
  const caps: Capability[] = [];
  for (const c of columns) {
    const cap = ACTION_COLUMN_CAPABILITY[c];
    if (cap) caps.push(cap);
  }
  return actionBundleFor(caps);
}

/**
 * The WHOLE "Aktion" bundle — every action column's capability and field.
 * Derived from the catalogue, never stored. The picker's entry does NOT stand
 * for all of it at once — see {@link effectiveActionBundle}.
 */
export const ACTION_BUNDLE: Readonly<ActionBundle> = actionBundleFor(
  Object.values(ACTION_COLUMN_CAPABILITY),
);

/** The guest list's own "Aktion" column: the check-in button. */
const GUEST_LIST_ACTION_CAP: Capability = ACTION_COLUMN_CAPABILITY.checkInOutCTA;

/**
 * What the field picker's "Aktion" entry stands for, for THIS role:
 *
 *  - the role already carries an action right (closure included) ⇒ the bundle
 *    of exactly those rights — the entry then mirrors the "Aktion" columns the
 *    role's rights give it, and is granted once their fields are on the list.
 *    A door role with `guests:check-in` and `checkedIn` reads as granted; it
 *    is never nudged towards `guests:write`, which is ALSO the server-side
 *    write allowlist for the accept/decline decision;
 *  - the role carries none ⇒ the guest list's own column (check-in). That is
 *    what an admin searching "Aktion" among the fields is looking for, and it
 *    is the narrower right. Approve/decline follows by ticking "edit guests"
 *    in the grid — the entry then names the field that column still needs.
 */
export function effectiveActionBundle(ticked: Set<Capability>): ActionBundle {
  const effective = expandImplied(ticked);
  const held = ACTION_BUNDLE.caps.filter((c) => effective.has(c));
  return actionBundleFor(held.length > 0 ? held : [GUEST_LIST_ACTION_CAP]);
}

/**
 * The bundle's capabilities the ticked set (implied closure included) does not
 * yet carry. Together with the fields the allowlist withholds this decides
 * whether the picker shows the entry as an OFFER or as a granted status — and
 * the offer names exactly these, so the admin reads what the click turns on.
 */
export function missingActionCaps(
  bundle: Readonly<ActionBundle>,
  ticked: Set<Capability>,
): Capability[] {
  const effective = expandImplied(ticked);
  return bundle.caps.filter((c) => !effective.has(c));
}
